import { BellRingIcon, CalendarDaysIcon, CalendarRangeIcon, ClipboardListIcon, LifeBuoyIcon, ReceiptIcon, ShieldAlertIcon, TriangleAlertIcon, UserPlusIcon, ZapIcon, type LucideIcon } from 'lucide-react';
import type { ReportSubscriptionInput } from '@/lib/reports';

/**
 * Starting points for a new report. Each template is a complete draft (what / when / who / how) the editor opens
 * on; the person renames it, adjusts and saves. `scopes` says where it makes sense: org = Organization → Reports,
 * job = a job's Reports tab. Recipient roles are chosen per scope because job reports cannot address the whole org.
 */
export interface ReportTemplate {
  key: string;
  title: string;
  blurb: string;
  kind: 'schedule' | 'event';
  scopes: Array<'org' | 'job'>;
  icon: LucideIcon;
  /** Time zone is filled in by the editor (job or organization zone). */
  input: (scope: 'org' | 'job') => Omit<ReportSubscriptionInput, 'timezone'>;
}

const adminRole = (scope: 'org' | 'job') => (scope === 'job' ? ['job_admins'] : ['org_admin']);

export const REPORT_TEMPLATES: ReportTemplate[] = [
  {
    key: 'monday_numbers',
    title: 'Monday morning numbers',
    blurb: 'Last week in one email: what went out, who it reached, list changes and what the automation did.',
    kind: 'schedule',
    scopes: ['org', 'job'],
    icon: CalendarDaysIcon,
    input: (scope) => ({
      name: 'Monday morning numbers',
      description: 'Last week at a glance.',
      triggerKind: 'schedule',
      cron: '0 8 * * 1',
      windowKind: 'last_week',
      sections: scope === 'job' ? ['messages', 'contacts', 'keywords', 'automation', 'scheduled'] : ['messages', 'jobs', 'contacts', 'automation', 'scheduled'],
      format: 'summary',
      channels: ['email', 'in_app'],
      recipients: { roles: adminRole(scope) },
    }),
  },
  {
    key: 'daily_digest',
    title: 'Daily site digest',
    blurb: 'Every weekday morning: yesterday’s sends, sign-ups, keyword traffic and what is coming up today.',
    kind: 'schedule',
    scopes: ['org', 'job'],
    icon: ClipboardListIcon,
    input: (scope) => ({
      name: 'Daily site digest',
      triggerKind: 'schedule',
      cron: '0 7 * * 1-5',
      windowKind: 'since_last',
      sections: scope === 'job' ? ['messages', 'scheduled', 'contacts', 'keywords'] : ['messages', 'scheduled', 'jobs', 'contacts'],
      format: 'summary',
      channels: ['email', 'in_app'],
      recipients: { roles: scope === 'job' ? ['job_admins'] : ['org_admin', 'job_admins'] },
    }),
  },
  {
    key: 'delivery_problems',
    title: 'Delivery problems',
    blurb: 'A notice when a send fails outright or when a large send loses a fifth of its recipients.',
    kind: 'event',
    scopes: ['org', 'job'],
    icon: TriangleAlertIcon,
    input: (scope) => ({
      name: 'Delivery problems',
      description: 'Sends that failed or had an unusual failure share.',
      triggerKind: 'event',
      eventTypes: ['message.failed', 'message.completed'],
      eventConditions: { minCount: 1, minRecipients: 10, minFailedPct: 20 },
      cooldownMinutes: 30,
      sections: [],
      channels: ['email', 'in_app'],
      recipients: { roles: adminRole(scope) },
    }),
  },
  {
    key: 'list_changes',
    title: 'Sign-ups & opt-outs',
    blurb: 'An hourly batch of who joined by text, who opted out and any spreadsheet imports.',
    kind: 'event',
    scopes: ['org', 'job'],
    icon: UserPlusIcon,
    input: (scope) => ({
      name: 'Sign-ups & opt-outs',
      triggerKind: 'event',
      eventTypes: ['contact.joined', 'contact.opted_out', 'contact.imported'],
      eventConditions: { minCount: 1 },
      cooldownMinutes: 60,
      sections: ['contacts'],
      channels: ['in_app', 'email'],
      recipients: { roles: adminRole(scope) },
    }),
  },
  {
    key: 'automation_watch',
    title: 'Automation watch',
    blurb: 'Know right away when a weather or schedule rule fails or is skipped.',
    kind: 'event',
    scopes: ['org', 'job'],
    icon: ZapIcon,
    input: (scope) => ({
      name: 'Automation watch',
      triggerKind: 'event',
      eventTypes: ['automation.run'],
      eventConditions: { minCount: 1, outcomes: ['failed', 'skipped'] },
      cooldownMinutes: 0,
      sections: [],
      channels: ['email', 'in_app'],
      recipients: { roles: adminRole(scope) },
    }),
  },
  {
    key: 'friday_wrap',
    title: 'Friday wrap-up',
    blurb: 'The week so far, every Friday afternoon, with the row-by-row detail and a CSV for the file.',
    kind: 'schedule',
    scopes: ['org', 'job'],
    icon: CalendarRangeIcon,
    input: (scope) => ({
      name: 'Friday wrap-up',
      triggerKind: 'schedule',
      cron: '0 16 * * 5',
      windowKind: 'week_to_date',
      sections: scope === 'job' ? ['messages', 'automation', 'keywords', 'contacts'] : ['messages', 'jobs', 'automation', 'keywords', 'contacts'],
      format: 'detailed',
      attachCsv: true,
      channels: ['email'],
      recipients: { roles: adminRole(scope) },
    }),
  },
  {
    key: 'security_watch',
    title: 'Sign-in security watch',
    blurb: 'A notice when failed sign-ins pile up, plus who joined the organization.',
    kind: 'event',
    scopes: ['org'],
    icon: ShieldAlertIcon,
    input: () => ({
      name: 'Sign-in security watch',
      triggerKind: 'event',
      eventTypes: ['auth.login_failed', 'user.joined'],
      eventConditions: { minCount: 5 },
      cooldownMinutes: 60,
      sections: ['people'],
      channels: ['email', 'in_app'],
      recipients: { roles: ['org_admin'] },
    }),
  },
  {
    key: 'help_desk',
    title: 'Help request activity',
    blurb: 'Every time someone files, answers or closes a help request.',
    kind: 'event',
    scopes: ['org'],
    icon: LifeBuoyIcon,
    input: () => ({
      name: 'Help request activity',
      triggerKind: 'event',
      eventTypes: ['support.updated'],
      eventConditions: { minCount: 1 },
      cooldownMinutes: 0,
      sections: [],
      channels: ['in_app', 'email'],
      recipients: { roles: ['org_admin'] },
    }),
  },
  {
    key: 'monthly_statement',
    title: 'Monthly statement',
    blurb: 'On the 1st: last month’s usage by job, billing position and payments, detailed with a CSV.',
    kind: 'schedule',
    scopes: ['org'],
    icon: ReceiptIcon,
    input: () => ({
      name: 'Monthly statement',
      description: 'Usage and billing for the previous month.',
      triggerKind: 'schedule',
      cron: '0 9 1 * *',
      windowKind: 'last_month',
      sections: ['billing', 'messages', 'jobs', 'contacts', 'people'],
      format: 'detailed',
      attachCsv: true,
      channels: ['email', 'in_app'],
      recipients: { roles: ['org_admin'] },
    }),
  },
  {
    key: 'keyword_pulse',
    title: 'Keyword pulse',
    blurb: 'A same-day notice every time workers text a keyword or JOIN, batched every four hours.',
    kind: 'event',
    scopes: ['org', 'job'],
    icon: BellRingIcon,
    input: (scope) => ({
      name: 'Keyword pulse',
      triggerKind: 'event',
      eventTypes: ['keyword.hit', 'contact.joined'],
      eventConditions: { minCount: 1 },
      cooldownMinutes: 240,
      sections: ['keywords'],
      channels: ['in_app'],
      recipients: { roles: scope === 'job' ? ['job_users'] : ['job_admins'] },
    }),
  },
];

export function templatesFor(scope: 'org' | 'job'): ReportTemplate[] {
  return REPORT_TEMPLATES.filter((t) => t.scopes.includes(scope));
}

/** Blank drafts for "start from scratch". */
export function blankTemplate(kind: 'schedule' | 'event', scope: 'org' | 'job'): Omit<ReportSubscriptionInput, 'timezone'> {
  return kind === 'schedule'
    ? { name: '', triggerKind: 'schedule', cron: '0 7 * * 1-5', windowKind: 'since_last', sections: scope === 'job' ? ['messages', 'contacts'] : ['messages', 'jobs'], format: 'summary', channels: ['email', 'in_app'], recipients: { roles: adminRole(scope) } }
    : { name: '', triggerKind: 'event', eventTypes: [], eventConditions: { minCount: 1 }, cooldownMinutes: 0, sections: [], channels: ['email', 'in_app'], recipients: { roles: adminRole(scope) } };
}
